import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { ArticleService } from 'src/app/services/article.service';
import { DiseaseService } from 'src/app/services/disease.service';
import { FEATURE_TYPES, EXTERNAL_POPUP_TEXT_EN } from 'shared/constants';
import { Section as SEC, Page as TABS, SubSection as SS } from 'shared/location-enums';
import { Feature } from 'shared/salesforce-types';
import { Article, Disease } from 'shared/types';

const HPO_URL = 'https://hpo.jax.org/';
const ALL_VIEW_THRESHOLD = 15;
const CLAMP_ROW_COUNT = 7;
const CLAMP_ITEM_THRESHOLD = 10;
const GROUP_HEADER_HEIGHT = 56;
const DESKTOP_LAYOUT_QUERY = '(min-width: 521px)';
const MIN_SEARCH_LENGTH = 2;

type SymptomsViewMode = 'default' | 'all';
type FrequencyGroupKey = 'always' | 'common' | 'uncommon' | 'unknown';
type SymptomsAnalyticsEvent =
    | { event: 'symptom_show_all_click' }
    | { event: 'symptom_list_toggle'; toggle_state: 'expand' | 'collapse' }
    | { event: 'symptom_search_start' }
    | { event: 'symptom_search_query'; search_term: string };

interface FrequencyGroupDefinition {
    key: FrequencyGroupKey;
    title: string;
    note: string;
    noteOne: string;
}

interface SymptomListItem {
    displayName: string;
    medicalTerm: string;
    showMedicalTerm: boolean;
    frequencyGroup: FrequencyGroupKey;
    isUnrelated: boolean;
    searchText: string;
}

interface SymptomFrequencyGroup extends FrequencyGroupDefinition {
    symptoms: SymptomListItem[];
}

const FREQUENCY_GROUPS: FrequencyGroupDefinition[] = [
    {
        key: 'always',
        title: 'Always',
        note: 'Nearly everyone has these, with rare exceptions.',
        noteOne: 'Nearly everyone has this, with rare exceptions.',
    },
    {
        key: 'common',
        title: 'Common',
        note: 'Many people have these, but not everyone.',
        noteOne: 'Many people have this, but not everyone.',
    },
    {
        key: 'uncommon',
        title: 'Uncommon',
        note: 'Some people have these, but most people do not.',
        noteOne: 'Some people have this, but most people do not.',
    },
    {
        key: 'unknown',
        title: 'Unknown',
        note: "We don't have enough information about how common these are.",
        noteOne: "We don't have enough information about how common this is.",
    },
];

@Component({
    selector: 'symptoms-section',
    templateUrl: './symptoms-section.component.html',
    styleUrls: ['./symptoms-section.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SymptomsSectionComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('symptomsSection') symptomsSectionRef: ElementRef<HTMLElement>;
    @ViewChild('clampedList') clampedListRef: ElementRef<HTMLDivElement>;
    @ViewChild('symptomsPanel') symptomsPanelRef: ElementRef<HTMLDivElement>;
    @ViewChild('viewToggleLine') viewToggleLineRef: ElementRef<HTMLDivElement>;
    @ViewChild('searchWrap') searchWrapRef: ElementRef<HTMLDivElement>;
    @ViewChild('moreButton') moreButtonRef: ElementRef<HTMLButtonElement>;

    searchForm = new UntypedFormGroup({ term: new UntypedFormControl('') });
    jaxorg: string = HPO_URL;

    hpoText: string;
    intro: Article;
    isNull = false;

    activeSymptomsView: SymptomsViewMode = 'default';
    allSymptoms: SymptomListItem[] = [];
    defaultSymptoms: SymptomListItem[] = [];
    unrelatedSymptoms: SymptomListItem[] = [];
    searchTerm = '';
    isDesktopLayout = false;
    isListExpanded = false;
    isViewToggleStuck = false;
    showSymptomsTopButton = false;
    stickySearchTop = 0;
    groupStickyTop = 0;
    returnButtonLeft: number | null = null;

    private disease: Disease;
    private sub = new Subscription();
    private measuredClampHeight: number | null = null;
    private clampMeasurementFrame: number | null = null;
    private hasStartedSearch = false;

    constructor(@Inject(DOCUMENT) private document: Document, private diseaseService: DiseaseService, private articleService: ArticleService) {
        this.disease = this.diseaseService.disease;

        if (SymptomsSectionComponent.isNull(this.disease)) {
            this.isNull = true;
            return;
        }

        this.intro = this.articleService.getOne(TABS.about, SEC.symptoms, SS.parent);
        this.isDesktopLayout = this.matchesDesktopLayout();

        const hpoKA: Article = this.articleService.getOne(TABS.about, SEC.symptoms, SS.resources);
        this.hpoText = hpoKA?.title?.replace('${HPOLink}', '') || 'Source: ';

        this.buildSymptomsViewModel();

        const searchTermChanges = this.searchForm.controls.term.valueChanges.pipe(distinctUntilChanged());

        this.sub.add(
            searchTermChanges.subscribe(term => {
                this.searchTerm = term || '';

                if (!this.hasStartedSearch && this.searchTerm.length > 0) {
                    this.hasStartedSearch = true;
                    this.pushAnalyticsEvent({ event: 'symptom_search_start' });
                }

                this.scheduleLayoutMeasurement();
            }),
        );

        this.sub.add(
            searchTermChanges.pipe(debounceTime(700)).subscribe(term => {
                const searchTerm = term || '';

                if (searchTerm.length >= 3) {
                    this.pushAnalyticsEvent({ event: 'symptom_search_query', search_term: searchTerm });
                }
            }),
        );
    }

    ngOnInit(): void {
        if (!this.isNull) {
            this.updateSymptomsTopButton();
        }
    }

    ngAfterViewInit(): void {
        if (!this.isNull) {
            this.scheduleLayoutMeasurement();
        }
    }

    static isNull(disease?: Disease) {
        return !(disease?.symptoms || []).some(symptom => SymptomsSectionComponent.isSymptomFeature(symptom));
    }

    ngOnDestroy(): void {
        const windowRef = this.document.defaultView;

        if (windowRef && this.clampMeasurementFrame !== null) {
            windowRef.cancelAnimationFrame(this.clampMeasurementFrame);
        }

        this.sub.unsubscribe();
    }

    onWindowResize() {
        const isDesktopLayout = this.matchesDesktopLayout();

        if (isDesktopLayout !== this.isDesktopLayout) {
            this.isDesktopLayout = isDesktopLayout;
            this.isListExpanded = false;
            this.measuredClampHeight = null;
        }

        this.scheduleLayoutMeasurement();
    }

    onWindowScroll() {
        this.updateSymptomsTopButton();
        this.updateViewToggleState();
    }

    setActiveSymptomsView(view: SymptomsViewMode) {
        if (view === this.activeSymptomsView) {
            return;
        }

        this.activeSymptomsView = view;
        this.isListExpanded = false;
        this.measuredClampHeight = null;
        this.clearSearchTerm();
        this.scheduleLayoutMeasurement();
    }

    toggleActiveSymptomsView() {
        const nextView = this.activeSymptomsView === 'default' ? 'all' : 'default';

        if (nextView === 'all') {
            this.pushAnalyticsEvent({ event: 'symptom_show_all_click' });
        }

        this.setActiveSymptomsView(nextView);
    }

    toggleCurrentListExpansion() {
        this.isListExpanded = !this.isListExpanded;
        this.pushAnalyticsEvent({ event: 'symptom_list_toggle', toggle_state: this.isListExpanded ? 'expand' : 'collapse' });
        this.scheduleLayoutMeasurement();
    }

    scrollToSymptomsTop() {
        this.symptomsSectionRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    onSymptomsTopAction() {
        if (this.isListExpanded && !this.hasSearchTerm) {
            this.isListExpanded = false;
            this.scheduleLayoutMeasurement();
        }

        this.scrollToSymptomsTop();
    }

    redirectToHPO(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();
        if (window.confirm(EXTERNAL_POPUP_TEXT_EN)) {
            const tab = window.open(HPO_URL, '_blank', 'noopener');
            if (tab) {
                tab.opener = null;
            }
        }
    }

    private pushAnalyticsEvent(event: SymptomsAnalyticsEvent): void {
        const windowRef = this.document.defaultView as (Window & { dataLayer?: SymptomsAnalyticsEvent[] }) | null;

        if (!windowRef) {
            return;
        }

        windowRef.dataLayer = windowRef.dataLayer || [];
        windowRef.dataLayer.push(event);
    }

    get showViewToggle(): boolean {
        return !this.isDesktopLayout && this.hasMobileAllView;
    }

    get hasMobileAllView(): boolean {
        return this.allSymptoms.length > ALL_VIEW_THRESHOLD;
    }

    get activeBaseSymptoms(): SymptomListItem[] {
        if (this.isDesktopLayout) {
            return this.allSymptoms;
        }

        return this.activeSymptomsView === 'all' ? this.allSymptoms : this.defaultSymptoms;
    }

    get filteredSymptoms(): SymptomListItem[] {
        return this.filterSymptoms(this.hasSearchTerm ? this.allSymptoms : this.activeBaseSymptoms);
    }

    get filteredUnrelatedSymptoms(): SymptomListItem[] {
        return this.filterSymptoms(this.unrelatedSymptoms);
    }

    get visibleGroups(): SymptomFrequencyGroup[] {
        return FREQUENCY_GROUPS.map(group => ({
            ...group,
            symptoms: this.filteredSymptoms.filter(symptom => symptom.frequencyGroup === group.key),
        })).filter(group => group.symptoms.length);
    }

    get primaryGroups(): SymptomFrequencyGroup[] {
        if (!this.isDesktopLayout) {
            return this.visibleGroups;
        }

        return this.splitDesktopGroups(this.visibleGroups).primary;
    }

    get secondaryGroups(): SymptomFrequencyGroup[] {
        if (!this.isDesktopLayout) {
            return [];
        }

        return this.splitDesktopGroups(this.visibleGroups).secondary;
    }

    get isSingleColumnDesktopPanel(): boolean {
        return this.isDesktopLayout && this.secondaryGroups.length === 0;
    }

    get showSearch(): boolean {
        return this.activeBaseSymptoms.length > ALL_VIEW_THRESHOLD;
    }

    get hasSearchTerm(): boolean {
        return this.normalizedSearchTerm.length >= MIN_SEARCH_LENGTH;
    }

    get hasSearchMatches(): boolean {
        return this.filteredSymptoms.length > 0 || this.filteredUnrelatedSymptoms.length > 0;
    }

    get isCurrentListExpanded(): boolean {
        return this.isListExpanded;
    }

    get shouldClampCurrentList(): boolean {
        return !this.hasSearchTerm && this.measuredClampHeight !== null;
    }

    get currentListMaxHeight(): number | null {
        return this.shouldClampCurrentList && !this.isCurrentListExpanded ? this.measuredClampHeight : null;
    }

    get currentListToggleLabel(): string {
        return this.isCurrentListExpanded ? 'Show less' : 'Show more';
    }

    get unrelatedNoteLead(): string {
        if (this.filteredUnrelatedSymptoms.length === 1) {
            return "People often think this is also a symptom. But it's";
        }

        return "People often think these are also symptoms. But they're";
    }

    get searchStatusMessage(): string {
        if (!this.hasSearchTerm) {
            return '';
        }

        const matchCount = this.filteredSymptoms.length + this.filteredUnrelatedSymptoms.length;
        return `${matchCount} symptom${matchCount === 1 ? '' : 's'} found.`;
    }

    get symptomsTopButtonLabel(): string {
        return this.isListExpanded && !this.hasSearchTerm ? 'Show less' : 'Symptoms';
    }

    get symptomsTopButtonAriaLabel(): string {
        return this.isListExpanded && !this.hasSearchTerm ? 'Show fewer symptoms' : 'Back to top of symptoms';
    }

    private buildSymptomsViewModel() {
        const symptoms = (this.disease.symptoms || [])
            .filter(symptom => SymptomsSectionComponent.isSymptomFeature(symptom))
            .map(symptom => this.mapSymptom(symptom))
            .sort(this.sortSymptomsByDisplayName);

        this.allSymptoms = symptoms.filter(symptom => !symptom.isUnrelated);
        this.unrelatedSymptoms = symptoms.filter(symptom => symptom.isUnrelated);

        const highFrequencySymptoms = this.allSymptoms.filter(symptom => symptom.frequencyGroup === 'always' || symptom.frequencyGroup === 'common');
        this.defaultSymptoms = this.hasMobileAllView && highFrequencySymptoms.length ? highFrequencySymptoms : this.allSymptoms;
    }

    private mapSymptom(symptom: Feature): SymptomListItem {
        const medicalTerm = (symptom.HPO_Name__c || '').trim();
        const displayName = this.getDisplayName(symptom, medicalTerm);
        const frequency = symptom.HPO_Frequency__c || '';
        const isUnrelated = this.normalizeText(frequency) === 'excluded (0%)';
        const frequencyGroup = this.getFrequencyGroup(frequency);
        const synonym = symptom.HPO_Synonym__c || '';
        const description = symptom.HPO_Description__c || '';
        const showMedicalTerm = !!medicalTerm && this.normalizeText(displayName) !== this.normalizeText(medicalTerm);

        return {
            displayName,
            medicalTerm,
            showMedicalTerm,
            frequencyGroup,
            isUnrelated,
            searchText: [displayName, medicalTerm, synonym, description].map(value => this.normalizeSearchText(value)).join(' '),
        };
    }

    private getDisplayName(symptom: Feature, fallbackName: string): string {
        return (
            [symptom.Display_HPO_Name__c, symptom.HPO_Name_map__c, symptom.HPO_NAME_MAPPING__c, fallbackName].map(name => (name || '').trim()).find(name => !!name) ||
            fallbackName.trim()
        );
    }

    private getFrequencyGroup(frequency: string): FrequencyGroupKey {
        switch (this.normalizeText(frequency)) {
            case 'always (100%)':
                return 'always';
            case 'very frequent (80-99%)':
            case 'frequent (30-79%)':
                return 'common';
            case 'occasional (5-29%)':
            case 'uncommon (<1-4%)':
                return 'uncommon';
            default:
                return 'unknown';
        }
    }

    private filterSymptoms(symptoms: SymptomListItem[]): SymptomListItem[] {
        const term = this.normalizedSearchTerm;

        if (term.length < MIN_SEARCH_LENGTH) {
            return symptoms;
        }

        return symptoms.filter(symptom => symptom.searchText.includes(term));
    }

    private clearSearchTerm() {
        this.searchTerm = '';
        this.searchForm.controls.term.setValue('', { emitEvent: false });
    }

    private splitDesktopGroups(groups: SymptomFrequencyGroup[]): { primary: SymptomFrequencyGroup[]; secondary: SymptomFrequencyGroup[] } {
        if (groups.length <= 1) {
            return { primary: groups, secondary: [] };
        }

        const primaryKeys: FrequencyGroupKey[] = ['always', 'common'];
        const secondaryKeys: FrequencyGroupKey[] = ['uncommon', 'unknown'];
        const primarySide = groups.filter(group => primaryKeys.includes(group.key));
        const secondarySide = groups.filter(group => secondaryKeys.includes(group.key));

        if (primarySide.length && secondarySide.length) {
            return { primary: primarySide, secondary: secondarySide };
        }

        return {
            primary: groups.filter((group, index) => index % 2 === 0),
            secondary: groups.filter((group, index) => index % 2 === 1),
        };
    }

    private scheduleLayoutMeasurement() {
        const windowRef = this.document.defaultView;

        if (!windowRef) {
            return;
        }

        if (this.clampMeasurementFrame !== null) {
            windowRef.cancelAnimationFrame(this.clampMeasurementFrame);
        }

        this.clampMeasurementFrame = windowRef.requestAnimationFrame(() => {
            this.clampMeasurementFrame = null;
            this.measureClampHeight();
            this.updateStickyOffsets();
            this.updateViewToggleState();
            this.updateSymptomsTopButton();
        });
    }

    private measureClampHeight() {
        const list = this.clampedListRef?.nativeElement;

        if (!list || this.hasSearchTerm) {
            this.measuredClampHeight = null;
            return;
        }

        const columns = Array.from(list.querySelectorAll<HTMLElement>('[data-symptoms-column]'));
        const columnMetrics = columns.map(column => ({
            groupCount: column.querySelectorAll('[data-frequency-group]').length,
            rows: Array.from(column.querySelectorAll<HTMLElement>('[data-symptom-row]')),
        }));
        const populatedColumns = columnMetrics.filter(column => column.rows.length);

        if (!populatedColumns.length) {
            this.measuredClampHeight = null;
            return;
        }

        const longestColumn = populatedColumns.reduce((longest, column) => (column.rows.length > longest.rows.length ? column : longest));

        if (longestColumn.rows.length <= CLAMP_ITEM_THRESHOLD) {
            this.measuredClampHeight = null;
            return;
        }

        const firstRowHeight = longestColumn.rows[0].getBoundingClientRect().height || 32;
        const clampHeight = Math.round(longestColumn.groupCount * GROUP_HEADER_HEIGHT + CLAMP_ROW_COUNT * firstRowHeight);

        this.measuredClampHeight = list.scrollHeight > clampHeight + 12 ? clampHeight : null;
    }

    private updateStickyOffsets() {
        const toggleHeight = this.showViewToggle ? this.viewToggleLineRef?.nativeElement.getBoundingClientRect().height || 0 : 0;
        const searchHeight = this.showSearch ? this.searchWrapRef?.nativeElement.getBoundingClientRect().height || 0 : 0;

        this.stickySearchTop = toggleHeight;
        this.groupStickyTop = toggleHeight + searchHeight;
    }

    private updateViewToggleState() {
        const toggle = this.viewToggleLineRef?.nativeElement;
        const panel = this.symptomsPanelRef?.nativeElement;

        if (!this.showViewToggle || !toggle || !panel) {
            this.isViewToggleStuck = false;
            return;
        }

        this.isViewToggleStuck = panel.getBoundingClientRect().top <= toggle.getBoundingClientRect().height;
    }

    private updateSymptomsTopButton() {
        const section = this.symptomsSectionRef?.nativeElement;
        const panel = this.symptomsPanelRef?.nativeElement;

        if (!section || !panel) {
            this.showSymptomsTopButton = false;
            this.returnButtonLeft = null;
            return;
        }

        const rect = section.getBoundingClientRect();
        const viewportHeight = this.document.defaultView?.innerHeight || this.document.documentElement.clientHeight;
        let shouldShow = rect.top < -40 && rect.bottom > viewportHeight;

        if (shouldShow && this.isListExpanded && !this.hasSearchTerm) {
            const moreButtonRect = this.moreButtonRef?.nativeElement.getBoundingClientRect();

            if (moreButtonRect && moreButtonRect.top < viewportHeight && moreButtonRect.bottom > 0) {
                shouldShow = false;
            }
        }

        const panelRect = panel.getBoundingClientRect();
        this.returnButtonLeft = Math.round(panelRect.left + panelRect.width / 2);
        this.showSymptomsTopButton = shouldShow;
    }

    private matchesDesktopLayout(): boolean {
        return this.document.defaultView?.matchMedia(DESKTOP_LAYOUT_QUERY).matches ?? true;
    }

    private normalizeText(value: string): string {
        return (value || '').trim().toLowerCase();
    }

    private get normalizedSearchTerm(): string {
        return this.normalizeSearchText(this.searchTerm);
    }

    private normalizeSearchText(value: string): string {
        return (value || '')
            .normalize('NFD')
            .toLowerCase()
            .replace(/[^\p{L}\p{N}]/gu, '');
    }

    private sortSymptomsByDisplayName(a: SymptomListItem, b: SymptomListItem) {
        return a.displayName.localeCompare(b.displayName, 'en', { sensitivity: 'base' });
    }

    private static isSymptomFeature(symptom: Feature): boolean {
        return !!symptom.HPO_Name__c && symptom.Feature_Type__c?.toLowerCase() === FEATURE_TYPES.symptom.toLowerCase();
    }
}
